import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Avatar,
  Divider,
  Paper,
  List,
  ListItem,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  InputAdornment,
  Menu,
  ListItemIcon,
  Alert,
  Skeleton,
  Stack
} from '@mui/material';
import {
  Search as SearchIcon,
  Groups as GroupsIcon,
  CalendarToday as CalendarIcon,
  AccountBalance as LoanIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Payment as DisburseIcon,
  Print as PrintIcon,
  WhatsApp as WhatsAppIcon,
  MoreVert as MoreIcon,
  AccountBalance as MoneyIcon,
  Schedule as ScheduleIcon,
  Receipt as ReceiptIcon,
  Person as PersonIcon,
  Refresh as RefreshIcon,
  TableChart as TableIcon,
  CalendarMonth as CalendarMonthIcon,
  DateRange as DateRangeIcon,
  CheckCircle as ReadyIcon 

} from '@mui/icons-material';

import { 
  fetchFormattedLoans, 
  approveLoan, 
  rejectLoan, 
  disburseLoan, 
  cancelLoan,
  fetchGroups,
  generateLoanPDF,
  updateMemberProfile
} from '../Users/api';
import PrintPreviewDialog from './PrintPreviewDialog';
import LoanApprovalAssessmentDialog from './LoanApprovalAssessmentDialog';
import Disbursement from './Disbursement';
// LoansTableView now lives at its own route (/management/loans/long-term,
// see App.js and the "Long-term Loans" sidebar item in lib/navigation.js)
// instead of being toggled in and out of this page with local state.

const Loans = ({ isDark = false } = {}) => {
  const navigate = useNavigate();
  // State management
  const [loans, setLoans] = useState([]);
  const [allLoadedLoans, setAllLoadedLoans] = useState([]);
  const [displayedLoans, setDisplayedLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [groupFilter, setGroupFilter] = useState('all');
  const [groups, setGroups] = useState([]);
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [actionLoanId, setActionLoanId] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [printLoan, setPrintLoan] = useState(null);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [approvingLoan, setApprovingLoan] = useState(null);

  // Date range filter
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: ''
  });
  const [selectedMonth, setSelectedMonth] = useState('');
  const [monthMenuAnchor, setMonthMenuAnchor] = useState(null);
  const [customDateDialogOpen, setCustomDateDialogOpen] = useState(false);
  const [tempDateRange, setTempDateRange] = useState({ startDate: '', endDate: '' });

  // Infinite scroll state
  const [hasMore, setHasMore] = useState(true);
  const observer = useRef();
  const [cursor, setCursor] = useState(null);
  const cursorRef = useRef(null);
  const loadingMoreRef = useRef(false);

  // Load initial data
  useEffect(() => {
    loadInitialLoans();
    loadGroups();
  }, []);

  // Handle filters change - reload from scratch
  useEffect(() => {
    handleFiltersChange();
  }, [statusFilter, groupFilter, dateRange, searchTerm]);

  // Keep refs in sync with state so the IntersectionObserver always has fresh values
useEffect(() => { cursorRef.current = cursor; }, [cursor]);

const lastLoanElementRef = useCallback(node => {
  if (observer.current) observer.current.disconnect();
  observer.current = new IntersectionObserver(entries => {
    if (entries[0].isIntersecting) {
      loadMoreLoans();
    }
  }, { threshold: 0.1 });
  if (node) observer.current.observe(node);
}, []); // stable — uses refs internally via loadMoreLoans

const handleFiltersChange = async () => {
  setCursor(null);
  cursorRef.current = null;
  setDisplayedLoans([]);
  setHasMore(true);
  await loadInitialLoans();
};

const loadInitialLoans = async () => {
  try {
    setLoading(true);
    setError(null);

    const response = await fetchFormattedLoans(
      null, // no cursor — fresh load
      statusFilter,
      '',
      groupFilter,
      searchTerm,
      dateRange.startDate,
      dateRange.endDate
    );

    if (response.success && response.data) {
      setDisplayedLoans(response.data);
      setCursor(response.next_cursor);
      cursorRef.current = response.next_cursor;
      setHasMore(response.has_more);
    } else {
      setDisplayedLoans([]);
      setHasMore(false);
    }
  } catch (error) {
    console.error('Error loading loans:', error);
    setError('Failed to load loans. Please try again.');
    setDisplayedLoans([]);
  } finally {
    setLoading(false);
  }
};

const loadMoreLoans = useCallback(async () => {
  if (loadingMoreRef.current || !cursorRef.current) return;

  loadingMoreRef.current = true;
  setLoadingMore(true);

  try {
    const response = await fetchFormattedLoans(
      cursorRef.current,
      statusFilter,
      '',
      groupFilter,
      searchTerm,
      dateRange.startDate,
      dateRange.endDate
    );

    if (response.success && response.data) {
      setDisplayedLoans(prev => [...prev, ...response.data]);
      setCursor(response.next_cursor);
      cursorRef.current = response.next_cursor;
      setHasMore(response.has_more);
    }
  } catch (error) {
    console.error('Error loading more loans:', error);
  } finally {
    loadingMoreRef.current = false;
    setLoadingMore(false);
  }
}, [statusFilter, groupFilter, searchTerm, dateRange]);

  const loadGroups = async () => {
    try {
      const groupsData = await fetchGroups();
      setGroups(groupsData);
    } catch (error) {
      console.error('Error loading groups:', error);
    }
  };

  const handleCustomDateApply = () => {
    setDateRange(tempDateRange);
    setSelectedMonth(''); // Clear month selection when custom date is applied
    setCustomDateDialogOpen(false);
  };

  const handleClearDateFilter = () => {
    setDateRange({ startDate: '', endDate: '' });
    setSelectedMonth('');
  };

  const getMonthName = (monthValue) => {
    if (!monthValue) return '';
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const [year, monthIndex] = monthValue.split('-');
    return `${monthNames[parseInt(monthIndex)]} ${year}`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return '#ed6c02';
      case 'approved': return '#0288d1';
      case 'disbursed': return '#2e7d32';
      case 'rejected': return '#d32f2f';
      case 'cancelled': return '#757575';
      default: return '#757575';
    }
  };

  const getStatusChipColor = (status) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'approved': return 'info';
      case 'disbursed': return 'success';
      case 'rejected': return 'error';
      case 'cancelled': return 'default';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <ScheduleIcon fontSize="small" />;
      case 'approved': return <ApproveIcon fontSize="small" />;
      case 'disbursed': return <MoneyIcon fontSize="small" />;
      case 'rejected': return <RejectIcon fontSize="small" />;
      case 'cancelled': return <RejectIcon fontSize="small" />;
      default: return null;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return `KSh ${parseFloat(amount || 0).toLocaleString()}`;
  };

  const handleAction = async (action, loan) => {
    setSelectedLoan(loan);
    setDialogType(action);
    setDialogOpen(true);
  };

  const handlePreviewDisbursement = (loan) => {
    setSelectedLoan(loan);
    setPreviewDialogOpen(true);
  };

  const handleApproveClick = (loan) => {
    setApprovingLoan(loan);
    setApproveDialogOpen(true);
  };
  
  const handleApproveSubmit = async (loan, notes) => {
    try {
      setActionLoading(true);
      
      const actionData = {
        loan_id: loan.database_id,
        notes: notes || 'Loan approved',
        officer_name: 'System User',
        timestamp: new Date().toISOString()
      };
  
      const response = await approveLoan(actionData);
      alert('Loan approved successfully');
      
      await loadInitialLoans();
      setApproveDialogOpen(false);
      setApprovingLoan(null);
      
    } catch (error) {
      console.error('Error approving loan:', error);
      alert(error.response?.data?.message || 'Failed to approve loan. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const executeAction = async () => {
    if (!selectedLoan) return;
    
    setActionLoading(true);
    
    try {
      const actionData = {
        loan_id: selectedLoan.database_id,
        notes: `${dialogType.charAt(0).toUpperCase() + dialogType.slice(1)} action performed`,
        officer_name: 'System User',
        timestamp: new Date().toISOString()
      };
  
      let response;
      let successMessage;
  
      switch (dialogType) {
        case 'reject':
          response = await rejectLoan(actionData);
          successMessage = 'Loan rejected successfully';
          break;
        case 'disburse':
          response = await disburseLoan(actionData);
          successMessage = 'Loan disbursed successfully';
          break;
        case 'cancel':
          response = await cancelLoan(actionData);
          successMessage = 'Loan cancelled successfully';
          break;
        default:
          throw new Error('Unknown action');
      }
  
      alert(successMessage);
      await loadInitialLoans();
      setDialogOpen(false);
      setSelectedLoan(null);
      
    } catch (error) {
      console.error(`Error ${dialogType} loan:`, error);
      alert(error.response?.data?.message || `Failed to ${dialogType} loan. Please try again.`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleMenuOpen = (event, loanId) => {
    setAnchorEl(event.currentTarget);
    setActionLoanId(loanId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setActionLoanId(null);
  };

  const handleWhatsAppAlert = (loan) => {
    const message = `*LOAN DISBURSEMENT REQUEST*\n\nLoan ID: ${loan.id}\nMember: ${loan.member.name}\nGroup: ${loan.group?.name || 'N/A'}\nPrincipal Amount: ${formatCurrency(loan.principalAmount)}\nNet Amount: ${formatCurrency(loan.netDisbursementAmount)}\n\nPlease process disbursement.`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const handlePrint = (loan) => {
    setPrintLoan(loan);
    setPrintDialogOpen(true);
    handleMenuClose(); // Close the menu if it was opened from there
  };
  
  const handlePrintSubmit = async (printData) => {
    try {
      // Step 1: Update member profile first
      const updateResponse = await updateMemberProfile(printData.member_id, printData.member_data);
      console.log('Member profile updated:', updateResponse);
  
      // Step 2: Generate PDF with loan_id only
      const pdfBlob = await generateLoanPDF(printData.loan_id);
      
      // Step 3: Create a URL for the blob and open it
      const url = window.URL.createObjectURL(pdfBlob);
      const printWindow = window.open(url, '_blank');
      
      // Optional: Trigger print dialog automatically
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print();
        };
      }
      
      // Step 4: Clean up
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 100);
      
      // Step 5: Close the dialog and show success
      setPrintDialogOpen(false);
      setPrintLoan(null);
      
      alert('Member profile updated and PDF generated successfully!');
      
      // Optional: Reload loans to reflect any changes
      await loadInitialLoans();
      
    } catch (error) {
      console.error('Error in print process:', error);
      
      // More detailed error message
      if (error.response?.data?.message) {
        alert(`Failed: ${error.response.data.message}`);
      } else if (error.message) {
        alert(`Failed: ${error.message}`);
      } else {
        alert('Failed to generate PDF. Please try again.');
      }
    }
  };

  const ActionDialog = () => (
    <Dialog 
      open={dialogOpen} 
      onClose={() => !actionLoading && setDialogOpen(false)} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
        }
      }}
    >
      <DialogTitle sx={{ 
        borderBottom: '1px solid #e0e0e0',
        backgroundColor: '#f8f9fa',
        fontWeight: 'bold',
        fontSize: '1.25rem'
      }}>
        {dialogType === 'reject' && 'Reject Loan'}
        {dialogType === 'disburse' && 'Disburse Loan'}
        {dialogType === 'cancel' && 'Cancel Loan'}
        {dialogType === 'details' && 'Loan Details'}
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        {selectedLoan && (
          <Box>
            <Grid container spacing={3}>
              {/* Member Information Card */}
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, backgroundColor: 'background.default', borderRadius: 2, border: 1, borderColor: 'divider' }}>
                  <Typography variant="h6" gutterBottom sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                    Member Information
                  </Typography>
                  <Box display="flex" alignItems="center" gap={2} mb={2}>
                    <Avatar sx={{ width: 60, height: 60, bgcolor: '#1976d2' }}>
                      {selectedLoan.member.name.charAt(0)}
                    </Avatar>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        {selectedLoan.member.name}
                      </Typography>
                      <Typography variant="body2" color="textSecondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <GroupsIcon fontSize="small" />
                        {selectedLoan.group?.name || 'No Group'}
                      </Typography>
                      <Typography variant="body2" color="textSecondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PersonIcon fontSize="small" />
                        {selectedLoan.member.phone}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </Grid>

              {/* Loan Information Card */}
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, backgroundColor: 'background.default', borderRadius: 2, border: 1, borderColor: 'divider' }}>
                  <Typography variant="h6" gutterBottom sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                    Loan Information
                  </Typography>
                  <Box sx={{ '& > *': { mb: 1 } }}>
                    <Typography><strong>Loan ID:</strong> {selectedLoan.id}</Typography>
                    <Typography><strong>Principal Amount:</strong> {formatCurrency(selectedLoan.principalAmount)}</Typography>
                    <Typography><strong>Repayment Amount:</strong> {formatCurrency(selectedLoan.repaymentAmount)}</Typography>
                    <Typography><strong>Net Disbursement:</strong> {formatCurrency(selectedLoan.netDisbursementAmount)}</Typography>
                    <Typography><strong>Monthly Repayment:</strong> {formatCurrency(selectedLoan.monthlyRepayment)}</Typography>
                    <Typography><strong>Installments:</strong> {selectedLoan.installments} months</Typography>
                    <Chip
                      icon={getStatusIcon(selectedLoan.status)}
                      label={selectedLoan.status.toUpperCase()}
                      color={getStatusChipColor(selectedLoan.status)}
                      size="small"
                      sx={{ mt: 1 }}
                    />
                  </Box>
                </Paper>
              </Grid>

              {/* Dates Information */}
              <Grid item xs={12}>
                <Paper sx={{ p: 2, backgroundColor: 'background.default', borderRadius: 2, border: 1, borderColor: 'divider' }}>
                  <Typography variant="h6" gutterBottom sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                    Important Dates
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CalendarIcon color="action" />
                        <Box>
                          <Typography variant="body2" color="textSecondary">Application Date</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                            {formatDate(selectedLoan.applicationDate)}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <DisburseIcon color="action" />
                        <Box>
                          <Typography variant="body2" color="textSecondary">Disbursement Date</Typography>
                          <Typography 
                            variant="body1" 
                            sx={{ 
                              fontWeight: 'bold',
                              color: selectedLoan.disbursementDate ? 'text.primary' : 'text.disabled'
                            }}
                          >
                            {formatDate(selectedLoan.disbursementDate)}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              {/* Deductions */}
              {selectedLoan.deductions && selectedLoan.deductions.length > 0 && (
                <Grid item xs={12}>
                  <Paper sx={{ p: 2, backgroundColor: 'background.default', borderRadius: 2, border: 1, borderColor: 'divider' }}>
                    <Typography variant="h6" gutterBottom sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                      Deductions Breakdown
                    </Typography>
                    <List dense>
                      {selectedLoan.deductions.map((deduction, index) => (
                        <ListItem 
                          key={index}
                          sx={{ 
                            backgroundColor: 'background.paper', 
                            borderRadius: 1, 
                            mb: 1,
                            border: 1,
                            borderColor: 'divider'
                          }}
                        >
                          <ListItemIcon>
                            <MoneyIcon color="primary" />
                          </ListItemIcon>
                          <ListItemText 
                            primary={
                              <Typography sx={{ fontWeight: 'bold' }}>
                                {deduction.type}
                              </Typography>
                            }
                            secondary={
                              <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold' }}>
                                {formatCurrency(deduction.amount)}
                              </Typography>
                            }
                          />
                        </ListItem>
                      ))}
                      <Divider sx={{ my: 1 }} />
                      <ListItem sx={{ backgroundColor: 'action.hover', borderRadius: 1, border: 2, borderColor: 'warning.main' }}>
                        <ListItemIcon>
                          <ReceiptIcon color="warning" />
                        </ListItemIcon>
                        <ListItemText 
                          primary={
                            <Typography sx={{ fontWeight: 'bold' }}>
                              Total Deductions
                            </Typography>
                          }
                          secondary={
                            <Typography variant="h5" color="warning.main" sx={{ fontWeight: 'bold' }}>
                              {formatCurrency(selectedLoan.principalAmount - selectedLoan.netDisbursementAmount)}
                            </Typography>
                          }
                        />
                      </ListItem>
                    </List>
                  </Paper>
                </Grid>
              )}
            </Grid>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2.5, borderTop: '1px solid #e0e0e0', backgroundColor: '#f8f9fa' }}>
        <Button 
          onClick={() => setDialogOpen(false)}
          variant="outlined"
          sx={{ minWidth: 100 }}
          disabled={actionLoading}
        >
          Cancel
        </Button>
        {dialogType !== 'details' && (
          <Button 
            onClick={executeAction}
            variant="contained" 
            color={dialogType === 'disburse' ? 'primary' : 'error'}
            startIcon={dialogType === 'disburse' ? <DisburseIcon /> : <RejectIcon />}
            sx={{ minWidth: 140 }}
            disabled={actionLoading}
          >
            {actionLoading ? 'Processing...' : `${dialogType.charAt(0).toUpperCase() + dialogType.slice(1)} Loan`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );

  if (error) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: '#1976d2' }}>
          Loans Management
        </Typography>
        <Alert 
          severity="error" 
          action={
            <Button color="inherit" size="small" onClick={loadInitialLoans}>
              <RefreshIcon /> Retry
            </Button>
          }
        >
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Search and Filters */}
      <Paper sx={{ p: 2, mb: 3, backgroundColor: 'background.paper' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              placeholder="Search by member name, loan ID, or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status"
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="disbursed">Disbursed</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Group</InputLabel>
              <Select
                value={groupFilter}
                onChange={(e) => setGroupFilter(e.target.value)}
                label="Group"
              >
                <MenuItem value="all">All Groups</MenuItem>
                {groups.map((group) => (
                  <MenuItem key={group.id} value={group.id.toString()}>{group.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="outlined"
              size="small"
              startIcon={<CalendarMonthIcon />}
              onClick={(e) => setMonthMenuAnchor(e.currentTarget)}
              sx={{ 
                justifyContent: 'flex-start',
                color: selectedMonth !== '' ? '#2563eb' : 'inherit',
                borderColor: selectedMonth !== '' ? '#2563eb' : 'inherit'
              }}
            >
              {selectedMonth !== '' ? getMonthName(selectedMonth) : 'Select Month'}
            </Button>
          </Grid>
          <Grid item xs={12} md={1.5}>
            <Button
              fullWidth
              variant="outlined"
              size="small"
              startIcon={<DateRangeIcon />}
              onClick={() => {
                setTempDateRange(dateRange);
                setCustomDateDialogOpen(true);
              }}
              sx={{ 
                color: (dateRange.startDate || dateRange.endDate) && selectedMonth === '' ? '#2563eb' : 'inherit',
                borderColor: (dateRange.startDate || dateRange.endDate) && selectedMonth === '' ? '#2563eb' : 'inherit'
              }}
            >
              Custom
            </Button>
          </Grid>
          <Grid item xs={12} md={1.5}>
            <Button
              fullWidth
              variant="contained"
              size="small"
              startIcon={<TableIcon />}
              onClick={() => navigate('/management/loans/long-term')}
              sx={{
                bgcolor: '#2563eb',
                '&:hover': { bgcolor: '#1d4ed8' }
              }}
            >
              Table
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Loading */}
      {loading && (
        <Grid container spacing={3}>
          {[...Array(6)].map((_, index) => (
            <Grid item xs={12} md={6} lg={4} key={index}>
              <Card>
                <CardContent>
                  <Skeleton variant="rectangular" width="100%" height={200} />
                  <Skeleton width="60%" sx={{ mt: 2 }} />
                  <Skeleton width="40%" />
                  <Skeleton width="80%" />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Loans Grid */}
      {!loading && (
        <Grid container spacing={3}>
          {displayedLoans.map((loan, index) => {
            const isLastElement = index === displayedLoans.length - 1;
            
            return (
              <Grid 
                item 
                xs={12} 
                md={6} 
                lg={4} 
                key={loan.id}
                ref={isLastElement ? lastLoanElementRef : null}
              >
              <Card sx={{ 
                height: '100%', 
                position: 'relative',
                borderTop: `4px solid ${getStatusColor(loan.status)}`,
                '&:hover': {
                  boxShadow: 3,
                  transform: 'translateY(-2px)',
                  transition: 'all 0.2s ease-in-out'
                }
              }}>
                <CardContent>
                  {/* Header with status and menu */}
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Box display="flex" gap={0.5} alignItems="center">
                      <Chip
                        icon={getStatusIcon(loan.status)}
                        label={loan.status.toUpperCase()}
                        color={getStatusChipColor(loan.status)}
                        size="small"
                      />
                      {/* Ready for Disbursement Badge */}
                      {loan.is_ready_for_disbursement && loan.status === 'approved' && (
                        <Chip
                          icon={<ReadyIcon sx={{ fontSize: '16px' }} />}
                          label="READY"
                          size="small"
                          sx={{
                            height: '24px',
                            backgroundColor: '#dcfce7',
                            color: '#16a34a',
                            fontWeight: 'bold',
                            fontSize: '0.7rem',
                            '& .MuiChip-icon': {
                              color: '#16a34a'
                            }
                          }}
                        />
                      )}
                    </Box>
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, loan.id)}
                    >
                      <MoreIcon />
                    </IconButton>
                  </Box>

                  {/* Member Info */}
                  <Box display="flex" alignItems="center" gap={2} mb={2}>
                    <Avatar sx={{ width: 48, height: 48, bgcolor: '#1976d2' }}>
                      {loan.member.name.charAt(0)}
                    </Avatar>
                    <Box>
                      <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
                        {loan.member.name}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {loan.group?.name || 'No Group'}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Loan Details */}
                  <Box mb={2}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Loan ID: {loan.id}
                    </Typography>
                    <Typography variant="h5" color="primary" sx={{ fontWeight: 'bold', my: 1 }}>
                      {formatCurrency(loan.principalAmount)}
                    </Typography>
                    <Typography variant="body2">
                      Repayment: {formatCurrency(loan.repaymentAmount)}
                    </Typography>
                    <Typography variant="body2">
                      Net Amount: {formatCurrency(loan.netDisbursementAmount)}
                    </Typography>
                    <Typography variant="body2">
                      Monthly: {formatCurrency(loan.monthlyRepayment)} × {loan.installments} months
                    </Typography>
                  </Box>

                  {/* Dates */}
                  <Box mb={2} sx={{ backgroundColor: 'action.hover', p: 1.5, borderRadius: 1, border: 1, borderColor: 'divider' }}>
                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="textSecondary" display="block">
                          Applied:
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {formatDate(loan.applicationDate)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="textSecondary" display="block">
                          Disbursed:
                        </Typography>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontWeight: 500,
                            color: loan.disbursementDate ? 'text.primary' : 'text.disabled'
                          }}
                        >
                          {formatDate(loan.disbursementDate)}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>

                  {/* Total Deductions Summary */}
                  {loan.deductions && loan.deductions.length > 0 && (
                    <Box mb={2} sx={{ backgroundColor: 'warning.lighter', p: 1.5, borderRadius: 1, border: 1, borderColor: 'warning.main' }}>
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="subtitle2" color="warning.dark">
                          Total Deductions:
                        </Typography>
                        <Typography variant="h6" color="warning.dark" sx={{ fontWeight: 'bold' }}>
                          {formatCurrency(loan.principalAmount - loan.netDisbursementAmount)}
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                        {loan.deductions.length} deduction{loan.deductions.length > 1 ? 's' : ''} applied • Click "View Details" for breakdown
                      </Typography>
                    </Box>
                  )}

                  {/* Action Buttons */}
                  <Box>
                    {loan.status === 'pending' && (
                      <Box display="flex" gap={1} mb={1}>
                        <Button
                          size="small"
                          variant="contained"
                          color="success"
                          startIcon={<ApproveIcon />}
                          onClick={() => handleApproveClick(loan)}
                          fullWidth
                        >
                          Approve
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          startIcon={<RejectIcon />}
                          onClick={() => handleAction('reject', loan)}
                          fullWidth
                        >
                          Reject
                        </Button>
                      </Box>
                    )}
                    
                    {loan.status === 'approved' && (
                      <Box>
                        <Button
                          size="small"
                          variant="contained"
                          color="primary"
                          startIcon={<DisburseIcon />}
                          onClick={() => handlePreviewDisbursement(loan)}
                          fullWidth
                          sx={{ mb: 1 }}
                        >
                          Disburse
                        </Button>
                        <Box display="flex" gap={1}>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<WhatsAppIcon />}
                            onClick={() => handleWhatsAppAlert(loan)}
                            sx={{ color: '#25D366', borderColor: '#25D366' }}
                          >
                            WhatsApp
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<PrintIcon />}
                            onClick={() => handlePrint(loan)}
                          >
                            Print
                          </Button>
                        </Box>
                      </Box>
                    )}

                    {loan.status === 'disbursed' && (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<ReceiptIcon />}
                        onClick={() => handleAction('details', loan)}
                        fullWidth
                      >
                        View Details
                      </Button>
                    )}

                    {(loan.status === 'approved' || loan.status === 'pending') && (
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        startIcon={<RejectIcon />}
                        onClick={() => handleAction('cancel', loan)}
                        fullWidth
                        sx={{ mt: 1 }}
                      >
                        Cancel Loan
                      </Button>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
        </Grid>
      )}

      {/* Loading More Indicator */}
      {loadingMore && (
        <Box display="flex" justifyContent="center" py={4}>
          <Stack direction="row" spacing={2}>
            <Skeleton variant="rectangular" width={350} height={400} sx={{ borderRadius: 2 }} />
            <Skeleton variant="rectangular" width={350} height={400} sx={{ borderRadius: 2 }} />
            <Skeleton variant="rectangular" width={350} height={400} sx={{ borderRadius: 2 }} />
          </Stack>
        </Box>
      )}

      {/* No More Loans Indicator */}
      {!loading && !hasMore && displayedLoans.length > 0 && (
        <Box textAlign="center" py={4}>
          <Typography variant="body2" color="text.secondary">
            No more loans to load
          </Typography>
        </Box>
      )}

      {/* No results */}
      {!loading && displayedLoans.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <LoanIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="textSecondary">
            No loans found
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {searchTerm || statusFilter !== 'all' || groupFilter !== 'all' || dateRange.startDate || dateRange.endDate
              ? 'Try adjusting your search or filter criteria'
              : 'No loan applications have been submitted yet'
            }
          </Typography>
        </Paper>
      )}

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => {
          const loan = displayedLoans.find(l => l.id === actionLoanId);
          handleAction('details', loan);
          handleMenuClose();
        }}>
          <ListItemIcon>
            <ReceiptIcon />
          </ListItemIcon>
          View Details
        </MenuItem>
        <MenuItem onClick={() => {
          const loan = displayedLoans.find(l => l.id === actionLoanId);
          handlePrint(loan);
          handleMenuClose();
        }}>
          <ListItemIcon>
            <PrintIcon />
          </ListItemIcon>
          Print Form
        </MenuItem>
        <MenuItem onClick={() => {
          const loan = displayedLoans.find(l => l.id === actionLoanId);
          handleWhatsAppAlert(loan);
          handleMenuClose();
        }}>
          <ListItemIcon>
            <WhatsAppIcon />
          </ListItemIcon>
          Send WhatsApp Alert
        </MenuItem>
      </Menu>

      {/* Print Preview Dialog */}
      <PrintPreviewDialog
        open={printDialogOpen}
        loan={printLoan}
        onClose={() => {
          setPrintDialogOpen(false);
          setPrintLoan(null);
        }}
        onPrint={handlePrintSubmit}
      />

      {/* Disbursement Dialog */}
      <Disbursement
        open={previewDialogOpen}
        loan={selectedLoan}
        onClose={() => {
          setPreviewDialogOpen(false);
          setSelectedLoan(null);
        }}
        onSuccess={loadInitialLoans}
      />

      {/* Action Dialog */}
      <ActionDialog />

      {/* Approve Dialog */}
      <LoanApprovalAssessmentDialog
  open={approveDialogOpen}
  loan={approvingLoan}
  onClose={() => { setApproveDialogOpen(false); setApprovingLoan(null); }}
  onApprove={handleApproveSubmit}
  loading={actionLoading}
/>

      {/* Month Selection Menu */}
      <Menu
        anchorEl={monthMenuAnchor}
        open={Boolean(monthMenuAnchor)}
        onClose={() => setMonthMenuAnchor(null)}
      >
        {(() => {
          const currentDate = new Date();
          const currentYear = currentDate.getFullYear();
          const currentMonth = currentDate.getMonth();
          const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
          
          // Generate last 12 months starting from current month going backwards
          const months = [];
          for (let i = 0; i < 12; i++) {
            const date = new Date(currentYear, currentMonth - i, 1);
            const monthIndex = date.getMonth();
            const year = date.getFullYear();
            months.push({
              label: `${monthNames[monthIndex]} ${year}`,
              monthIndex: monthIndex,
              year: year,
              value: `${year}-${monthIndex}`
            });
          }
          
          return months.map((month, index) => (
            <MenuItem 
              key={month.value} 
              onClick={() => {
                const startDate = new Date(month.year, month.monthIndex, 1);
                const endDate = new Date(month.year, month.monthIndex + 1, 0);
                setDateRange({
                  startDate: startDate.toISOString().split('T')[0],
                  endDate: endDate.toISOString().split('T')[0]
                });
                setSelectedMonth(month.value);
                setMonthMenuAnchor(null);
              }}
              selected={selectedMonth === month.value}
              sx={{
                bgcolor: index === 0 && selectedMonth === '' ? 'primary.main' : 'inherit',
                color: index === 0 && selectedMonth === '' ? 'white' : 'inherit',
                '&:hover': {
                  bgcolor: 'action.hover'
                }
              }}
            >
              {month.label}
              {index === 0 && <Chip size="small" label="Current" sx={{ ml: 1, height: 20 }} />}
            </MenuItem>
          ));
        })()}
        <Divider />
        <MenuItem onClick={handleClearDateFilter}>
          <ListItemIcon>
            <RejectIcon fontSize="small" />
          </ListItemIcon>
          Clear Filter
        </MenuItem>
      </Menu>

      {/* Custom Date Range Dialog */}
      <Dialog 
        open={customDateDialogOpen} 
        onClose={() => setCustomDateDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Custom Date Range</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Start Date"
              type="date"
              value={tempDateRange.startDate}
              onChange={(e) => setTempDateRange({ ...tempDateRange, startDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="End Date"
              type="date"
              value={tempDateRange.endDate}
              onChange={(e) => setTempDateRange({ ...tempDateRange, endDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCustomDateDialogOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleClearDateFilter} color="error">
            Clear
          </Button>
          <Button 
            onClick={handleCustomDateApply} 
            variant="contained"
            sx={{ 
              bgcolor: '#2563eb',
              '&:hover': { bgcolor: '#1d4ed8' }
            }}
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Loans;